import { useCallback, useEffect, useState } from 'react';

import {
  moveArrayElement,
  SortingComparator,
  tableSort,
  OrderType,
} from '../utils/gridSorting';
import { addToLocalStorageObject } from '../utils/localStorageUtils';
import { ITableSettings } from '../types/types/table';

interface DefaultSettings<T> extends ITableSettings {
  sortingComparators?: SortingComparator<T>[];
}

export interface UseTableProps<T> {
  initialOrderBy?: string;
  initialOrder?: OrderType;
  initialColumnsOrderIds?: string[];
  initialHidedColumns?: string[];
  array: T[] | undefined;
  settingsName: string;
  defaultSettings: DefaultSettings<T>;
}

export interface UseTableReturns<T> {
  array: T[] | undefined;
  order: OrderType;
  orderBy: string;
  columnsOrderIds: string[];
  hidedColumns: string[];
  onSort: (
    order: OrderType,
    orderBy: string,
  ) => (event?: React.MouseEvent<HTMLElement>) => void;
  onSortColumns: (dragId: string, destinationId: string) => void;
  onToggleHideColumn: (columnId: string) => void;
  unHideAllColumn: () => void;
  sortedArray: T[];
  onRestoreDefault: () => void;
}

const useTable = <T>({
  initialOrderBy,
  initialOrder,
  initialColumnsOrderIds,
  initialHidedColumns,
  array,
  settingsName,
  defaultSettings,
}: UseTableProps<T>): UseTableReturns<T> => {
  const [orderBy, setOrderBy] = useState(
    initialOrderBy || defaultSettings.orderBy,
  );
  const [order, setOrder] = useState(initialOrder || defaultSettings.order);
  const [columnsOrderIds, setOrderColumnsIds] = useState<string[]>(
    initialColumnsOrderIds || defaultSettings.columnsOrderIds,
  );
  const [hidedColumns, setHidedColumns] = useState(
    initialHidedColumns || defaultSettings.hidedColumns,
  );
  const [sortedArray, setSortedArray] = useState(array || []);

  useEffect(() => {
    if (array?.length) {
      sortArray(initialOrder, initialOrderBy);
    } else {
      setSortedArray([]);
    }
  }, [array]);

  const onRequestSort = useCallback(
    (property: string) => {
      let newOrder = defaultSettings.order;

      if (orderBy === property) {
        newOrder = order === 'asc' ? 'desc' : 'asc';
      }

      setOrder(newOrder);
      setOrderBy(property);
      sortArray(newOrder, property);
    },
    [orderBy, order, array],
  );

  const onToggleHideColumn = useCallback(
    (columnId: string) => {
      const isHided = hidedColumns.includes(columnId);
      const columns = isHided
        ? hidedColumns.filter((id: string) => id !== columnId)
        : [...hidedColumns, columnId];
      setHidedColumns(columns);
      const orderColumns = isHided
        ? [...columnsOrderIds, columnId]
        : columnsOrderIds.filter((id: string) => id !== columnId);

      setOrderColumnsIds(orderColumns);
    },
    [columnsOrderIds, hidedColumns],
  );

  const unHideAllColumn = useCallback(() => setHidedColumns([]), []);

  const onSortColumns = useCallback(
    (dragId: string, destinationId: string) => {
      const startPosition = columnsOrderIds.indexOf(dragId);
      const endPosition = columnsOrderIds.indexOf(destinationId);
      setOrderColumnsIds(
        moveArrayElement(startPosition, endPosition, columnsOrderIds),
      );
    },
    [columnsOrderIds],
  );

  const onSort = useCallback(
    (order: OrderType, orderBy: string) =>
      (event?: React.MouseEvent<HTMLElement>) => {
        onRequestSort(orderBy);
      },
    [onRequestSort],
  );

  useEffect(() => {
    if (settingsName)
      addToLocalStorageObject(settingsName, {
        order,
        orderBy,
        hidedColumns,
        columnsOrderIds,
      });
  }, [order, orderBy, columnsOrderIds, settingsName, hidedColumns]);

  const onRestoreDefault = useCallback(() => {
    setOrderBy(defaultSettings.orderBy);
    setOrder(defaultSettings.order);
    setOrderColumnsIds(defaultSettings.columnsOrderIds);
    setHidedColumns(defaultSettings.hidedColumns);
    sortArray();
  }, [defaultSettings, array]);

  function sortArray(
    order = defaultSettings.order,
    orderBy = defaultSettings.orderBy,
  ) {
    const sortedArray = tableSort(
      [...(array || [])],
      order,
      orderBy,
      defaultSettings.sortingComparators,
    );
    setSortedArray(sortedArray);
  }

  return {
    array,
    order,
    orderBy,
    columnsOrderIds,
    hidedColumns,
    onSort,
    onSortColumns,
    onToggleHideColumn,
    unHideAllColumn,
    sortedArray,
    onRestoreDefault,
  };
};

export default useTable;
